import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Pagination, Select as AntSelect } from "antd";
import { Download, Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { PtsPurchaseApprovalService } from "@/services/APIs";
import type { PurchaseApprovalStatus } from "@/services/APIs/PtsPurchaseApprovalService";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import PurchaseApprovalFilters from "./PurchaseApprovalFilters";
import {
  EMPTY_PURCHASE_APPROVAL_FILTERS,
  type PurchaseApprovalFilterValues,
} from "./PurchaseApprovalFilters.constants";
import {
  formatDateYmd,
  formatMoney,
  formatPurchaseApprovalReason,
  resolveName,
  statusBadgeClass,
  statusLabel,
} from "./utils";
import { PurchaseApprovalPurchaseStatusBadge } from "./StatusBadge";
import ReasonModal from "./ReasonModal";
import ReasonCell from "./ReasonCell";
import PurchaseApprovalViewModal from "./PurchaseApprovalViewModal";
import { formatDateDmy } from "../LeaveRequests/utils";
import { useDragRowSelection } from "../../hooks/useDragRowSelection";

const MANAGEMENT_STATUSES: PurchaseApprovalStatus[] = [
  "Pending Approval",
  "Approved",
  "On-hold",
  "Reject",
];

const BULK_STATUS_OPTIONS = [
  { value: "Pending Approval", label: "Pending Approval" },
  { value: "Approved", label: "Approved" },
  { value: "On-hold", label: "On Hold" },
];

type ManagementTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
};

const ManagementTab = ({
  projectNameById,
  employeeNameById,
}: ManagementTabProps) => {
  const queryClient = useQueryClient();
  const [filterValues, setFilterValues] =
    useState<PurchaseApprovalFilterValues>(EMPTY_PURCHASE_APPROVAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);

  // Query for the paginated table list and stats
  const { data, isLoading } = useQuery({
    queryKey: [
      "purchase-approvals",
      "management-list",
      filterValues.projectId,
      filterValues.status,
      filterValues.purchaseStatus,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsPurchaseApprovalService.getPurchaseApprovals({
        page: currentPage,
        page_size: pageSize,
        project: filterValues.projectId,
        status: filterValues.status,
        purchase_status: filterValues.purchaseStatus,
      }),
  });

  const recentRequests = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const stats = data?.cards ?? {
    pending: 0,
    approval: 0,
    on_hold: 0,
    reject: 0,
  };

  const {
    selectedIds,
    setSelectedIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleMouseDown,
    handleMouseEnter,
  } = useDragRowSelection(recentRequests);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [filterValues, setSelectedIds]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, pageSize, setSelectedIds]);

  const bulkStatusMutation = useMutation({
    mutationFn: ({
      ids,
      status,
    }: {
      ids: string[];
      status: PurchaseApprovalStatus;
    }) => PtsPurchaseApprovalService.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      notify.success("Purchase requests status updated successfully");
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ["purchase-approvals"] });
    },
    onError: (error: any) => {
      notify.error(
        error?.message ?? "Failed to update status for selected items"
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      management_reason,
    }: {
      id: string;
      status: PurchaseApprovalStatus;
      management_reason?: string;
    }) =>
      PtsPurchaseApprovalService.updatePurchaseApproval(id, {
        status,
        ...(management_reason ? { management_reason } : {}),
      }),
    onSuccess: result => {
      if (!result?.id) {
        return;
      }
      notify.success("Purchase request status updated");
      setRejectTargetId(null);
      queryClient.invalidateQueries({ queryKey: ["purchase-approvals"] });
    },
  });

  const handleStatusChange = (
    id: string,
    status: PurchaseApprovalStatus,
    currentStatus: string
  ) => {
    if (status === currentStatus) {
      return;
    }
    if (status === "Reject") {
      setRejectTargetId(id);
      return;
    }
    statusMutation.mutate({ id, status });
  };

  const handleExportCSV = async () => {
    try {
      const res = await PtsPurchaseApprovalService.getPurchaseApprovals({
        page_size: 100000,
        project: filterValues.projectId,
        status: filterValues.status,
        purchase_status: filterValues.purchaseStatus,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "ID",
        "PR Number",
        "Project ID",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Date",
        "Cost",
        "Currency",
        "Frequency",
        "Requested By ID",
        "Requested By Name",
        "Description",
        "Status",
        "Purchase Status",
        "Reason",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const reqName = resolveName(item.requested_by, employeeNameById);

        return [
          escapeCsv(item.id),
          escapeCsv(item.pr_number),
          escapeCsv(item.project),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateDmy(item.requested_date)),
          escapeCsv(formatDateDmy(item.expected_date)),
          escapeCsv(item.cost),
          escapeCsv(item.currency ?? "INR"),
          escapeCsv(item.frequency),
          escapeCsv(item.requested_by),
          escapeCsv(reqName),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(item.purchase_status),
          escapeCsv(formatPurchaseApprovalReason(item)),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `purchase-approvals-management-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Management dashboard
        </h1>
        <Button
          type="button"
          variant="default"
          onClick={handleExportCSV}
          className="h-10 px-5"
        >
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <PurchaseApprovalFilters
        values={filterValues}
        onChange={setFilterValues}
        idPrefix="management-purchase-approval"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="mt-2 text-3xl font-bold text-amber-500">
              {stats.pending}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">On Hold</p>
            <p className="mt-2 text-3xl font-bold text-sky-600">
              {stats.on_hold}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">
              {stats.approval}
            </p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Reject</p>
            <p className="mt-2 text-3xl font-bold text-rose-600">
              {stats.reject}
            </p>
          </CardContent>
        </Card>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 rounded-lg border border-[#BAE0FF] bg-[#F0F7FF] px-4 py-2.5">
          <span className="text-sm font-medium text-gray-800">
            {selectedIds.length} selected
          </span>
          <div className="w-48">
            <AntSelect
              value={undefined}
              placeholder="Change Status"
              disabled={bulkStatusMutation.isPending}
              loading={bulkStatusMutation.isPending}
              onChange={value => {
                if (value) {
                  bulkStatusMutation.mutate({
                    ids: selectedIds,
                    status: value,
                  });
                }
              }}
              className="w-full"
              options={BULK_STATUS_OPTIONS}
            />
          </div>
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                  <th className="w-[48px] px-4 py-3 text-center">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      disabled={isLoading || recentRequests.length === 0}
                    />
                  </th>
                  <th className="px-5 py-3 font-medium">PR Number</th>
                  <th className="px-5 py-3 font-medium">Project</th>
                  <th className="px-5 py-3 font-medium">Title</th>
                  <th className="px-5 py-3 font-medium">Cost</th>
                  <th className="px-5 py-3 font-medium">Frequency</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Requested Date</th>
                  <th className="px-5 py-3 font-medium">Expected Date</th>
                  <th className="px-5 py-3 font-medium">Requested By</th>
                  <th className="px-5 py-3 font-medium">Purchase Status</th>
                  <th className="w-[88px] px-5 py-3 text-center font-medium">
                    Reason
                  </th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading purchase requests...
                    </td>
                  </tr>
                ) : recentRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={13}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No purchase requests found.
                    </td>
                  </tr>
                ) : (
                  recentRequests.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td
                        className="w-[48px] cursor-pointer select-none px-4 py-3.5 text-center"
                        onMouseDown={e => handleMouseDown(item.id, index, e)}
                        onMouseEnter={() => handleMouseEnter(item.id, index)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          className="pointer-events-none"
                        />
                      </td>
                      <td className="px-5 py-3.5 font-medium text-[#111827]">
                        {item.pr_number || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.project_name ??
                          resolveName(item.project, projectNameById)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.title || "—"}
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-[#374151]">
                        {formatMoney(item.cost, item.currency)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {item.frequency || "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <Select
                          value={String(item.status || "Pending Approval")}
                          disabled={
                            statusMutation.isPending &&
                            statusMutation.variables?.id === item.id
                          }
                          onValueChange={status =>
                            handleStatusChange(
                              item.id,
                              status,
                              String(item.status || "Pending Approval")
                            )
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              "h-8 w-[170px] rounded-full px-3 text-xs font-medium shadow-none focus:ring-1 focus:ring-offset-0",
                              statusBadgeClass(item.status)
                            )}
                          >
                            <SelectValue>
                              {statusLabel(item.status)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {MANAGEMENT_STATUSES.map(status => (
                              <SelectItem key={status} value={status}>
                                {statusLabel(status)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.requested_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {formatDateDmy(item.expected_date)}
                      </td>
                      <td className="px-5 py-3.5 text-[#374151]">
                        {resolveName(item.requested_by, employeeNameById)}
                      </td>
                      <td className="px-5 py-3.5">
                        <PurchaseApprovalPurchaseStatusBadge
                          purchaseStatus={item.purchase_status}
                        />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <ReasonCell item={item} />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewId(item.id)}
                          className="h-8 w-8 text-gray-600 hover:text-[#111827]"
                          aria-label="View purchase request"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center justify-end border-t p-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(page, size) => {
                  if (size && size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                    return;
                  }
                  setCurrentPage(page);
                }}
                onShowSizeChange={(_current, size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                pageSizeOptions={["50", "100", "200", "500"]}
                defaultPageSize={100}
                disabled={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonModal
        open={Boolean(rejectTargetId)}
        title="Reject purchase request"
        description="Please provide a reason for rejecting this purchase request."
        isSubmitting={statusMutation.isPending}
        onOpenChange={open => {
          if (!open && !statusMutation.isPending) {
            setRejectTargetId(null);
          }
        }}
        onSubmit={reason => {
          if (!rejectTargetId) {
            return;
          }
          statusMutation.mutate({
            id: rejectTargetId,
            status: "Reject",
            management_reason: reason,
          });
        }}
      />

      <PurchaseApprovalViewModal
        id={viewId}
        open={Boolean(viewId)}
        projectNameById={projectNameById}
        employeeNameById={employeeNameById}
        onOpenChange={open => {
          if (!open) {
            setViewId(null);
          }
        }}
      />
    </div>
  );
};

export default ManagementTab;
